var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, List, ListItem, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { lazy, useCallback, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { createExtraSettingsAction, updateExtraSettingsAction } from '../../../api/action/extraSettings.action';
import { QUERY_KEY } from '../../../api/types/query.type';
import { useSettings } from '../../../context';
import { createOrUpdateExtraSettingsFormSchema } from '../../../schemas/extraSettings.schemas';
import { FORM_INPUTS } from './form.costants';
import { formatUserName } from './helpers';
import { theme } from './theme';
var ButtonComponent = lazy(function () { return import('../../UIcomponents/ButtonComponent/ButtonComponent'); });
var AlertComponent = lazy(function () { return import('../../UIcomponents/AlertComponent/AlertComponent'); });
var Loader = lazy(function () { return import('../../UIcomponents/Loader'); });
var Select = lazy(function () { return import('../../UIcomponents/Select/Select'); });
var ExtraSettingsForm = function (_a) {
    var shopId = _a.shopId, uspacyFunnels = _a.uspacyFunnels, extraSettings = _a.extraSettings, portalUsers = _a.portalUsers;
    var _b = useState(false), needToShowErrorAlert = _b[0], setNeedToShowErrorAlert = _b[1];
    var t = useTranslation('settings').t;
    var userPortal = useSettings().userPortal;
    var currentFunnel = uspacyFunnels.find(function (funnel) { return funnel.id === (extraSettings === null || extraSettings === void 0 ? void 0 : extraSettings.funnel_id); });
    var currentPortalUser = portalUsers.find(function (user) { return user.id === (extraSettings === null || extraSettings === void 0 ? void 0 : extraSettings.responsible_person_id); });
    var _c = useForm({
        resolver: zodResolver(createOrUpdateExtraSettingsFormSchema()),
        defaultValues: {
            funnel_id: (currentFunnel === null || currentFunnel === void 0 ? void 0 : currentFunnel.id) || '',
            responsible_person_id: (currentPortalUser === null || currentPortalUser === void 0 ? void 0 : currentPortalUser.id) || '',
        },
        mode: 'all',
    }), control = _c.control, handleSubmit = _c.handleSubmit;
    var queryClient = useQueryClient();
    var _d = useMutation({
        mutationFn: createExtraSettingsAction,
        onSuccess: function () {
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEY.GET_EXTRASETTINGS, userPortal],
            });
        },
        onError: function () {
            setNeedToShowErrorAlert(true);
            setTimeout(function () {
                setNeedToShowErrorAlert(false);
            }, 2000);
        },
    }), createExtraSettings = _d.mutate, isCreatingExtraSettings = _d.isPending;
    var _e = useMutation({
        mutationFn: updateExtraSettingsAction,
        onSuccess: function () {
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEY.GET_EXTRASETTINGS, userPortal],
            });
        },
        onError: function () {
            setNeedToShowErrorAlert(true);
            setTimeout(function () {
                setNeedToShowErrorAlert(false);
            }, 2000);
        },
    }), updateExtraSettings = _e.mutate, isUpdatingExtraSettings = _e.isPending;
    var onSubmit = useCallback(function (data) {
        if (!(currentFunnel === null || currentFunnel === void 0 ? void 0 : currentFunnel.id)) {
            createExtraSettings(__assign({ shop_id: shopId }, data));
        }
        else {
            updateExtraSettings(__assign({ shop_id: shopId }, data));
        }
    }, [createExtraSettings, updateExtraSettings, shopId]);
    var isLoading = isCreatingExtraSettings || isUpdatingExtraSettings;
    if (isLoading) {
        return (_jsx(Box, { sx: theme.container, children: _jsx(Loader, {}) }));
    }
    if (needToShowErrorAlert) {
        return (_jsx(Box, { sx: theme.container, children: _jsx(AlertComponent, { type: "error", message: t('extraSettings.funnels.error.createFunnel') }) }));
    }
    return (_jsxs(Box, { component: "form", onSubmit: handleSubmit(onSubmit), sx: theme.container, children: [_jsx(List, { sx: theme.list, children: FORM_INPUTS.map(function (_a) {
                    var name = _a.name, label = _a.label, placeholder = _a.placeholder, description = _a.description;
                    return (_jsxs(ListItem, { sx: theme.listItem, children: [_jsx(Typography, { sx: theme.description, children: t(description) }), _jsx(Controller, { name: name, control: control, render: function (_a) {
                                    var field = _a.field, error = _a.fieldState.error;
                                    var options = name === 'funnel_id'
                                        ? uspacyFunnels.map(function (funnel) { return ({
                                            value: funnel.id,
                                            label: funnel.title,
                                        }); })
                                        : portalUsers.map(function (user) { return ({
                                            value: user.id,
                                            label: formatUserName(user.firstName, user.lastName),
                                        }); });
                                    return (_jsx(Select, __assign({}, field, { id: "".concat(label, "-select"), label: t(label), placeholder: t(placeholder), options: options, error: !!error, helperText: t((error === null || error === void 0 ? void 0 : error.message) || ''), renderValue: function (value) {
                                            if (!value)
                                                return '';
                                            if (name === 'funnel_id') {
                                                var funnel = uspacyFunnels.find(function (f) { return f.id === value; });
                                                return (funnel === null || funnel === void 0 ? void 0 : funnel.title) || '';
                                            }
                                            else {
                                                var user = portalUsers.find(function (u) { return u.id === value; });
                                                return user ? formatUserName(user.firstName, user.lastName) : '';
                                            }
                                        } })));
                                } }, label)] }, label));
                }) }), !(extraSettings === null || extraSettings === void 0 ? void 0 : extraSettings.funnel_id) ? (_jsx(ButtonComponent, { title: t('extraSettings.submit'), type: "submit", isDisabled: isCreatingExtraSettings })) : (_jsx(ButtonComponent, { title: t('extraSettings.update'), type: "submit", isDisabled: isUpdatingExtraSettings }))] }));
};
export default ExtraSettingsForm;
